/**
 * Deutsch — zugleich die **Vorlage** für die anderen Sprachen.
 *
 * `Messages` wird aus diesem Objekt abgeleitet; fehlt in `en.ts` oder `fr.ts` ein Schlüssel oder
 * hat er die falsche Form, meldet das der Compiler. Übersetzt wird nur die **Oberfläche** —
 * Prüfungen, Stationen, Aufgabentexte und Bausteine kommen vom Server und bleiben, wie sie sind.
 *
 * Die Formate stehen bewusst **im Katalog** und nicht im Code: das Dezimalzeichen ist eine
 * Eigenschaft der Sprache, keine der Punkterechnung.
 */
export const de = {
  label: 'Deutsch',
  /** BCP-47-Kennung, auch für `Intl` und das `lang`-Attribut. */
  tag: 'de',

  format: {
    /** Dezimalzeichen für Punktzahlen. */
    decimal: ',',
  },

  start: {
    resumeKicker: 'Angefangene Bewertung',
    resumeTitle: 'Weitermachen?',
    resumeBody: (prüfling: string, station: string) =>
      `Für ${prüfling} an ${station} liegt eine angefangene Bewertung vor.`,
    resumeKeep: 'Weitermachen',
    resumeFresh: 'Neu beginnen',
    resumeNote: 'Neu beginnen löscht nichts — die Bewertung bleibt gespeichert.',
    pickKicker: 'Prüfling',
    pickTitle: 'Wer wird bewertet?',
    scan: 'QR-Code scannen',
    fromList: 'Aus Liste wählen',
    startStation: 'Station starten',
  },

  /**
   * Die Rückstandspille in der Kopfzeile (`features/header/Rueckstandspille.tsx`).
   *
   * „Pakete" statt „Protokollzeilen": der Prüfer soll nicht wissen müssen, wie das Protokoll
   * innen aussieht — er soll sehen, dass etwas noch nicht beim Server ist.
   */
  backlog: {
    title: 'Noch nicht übertragen',
    packets: 'Pakete',
    files: 'Dateien',
    rejected: 'aufgegeben',
    kinds: {
      pakete: 'Bewertungen (Protokolleinträge)',
      screenshot: 'Bildschirmfotos des Bogens',
      photo: 'Fotos',
      video: 'Videos',
      audio: 'Audioaufnahmen',
      handwriting: 'Handschriftliche Notizen',
      annotation: 'Markierungen',
      sonstige: 'Sonstige Dateien',
    },
    rejectedRow: 'Davon aufgegeben',
    autoOff: 'Automatische Übertragung ist aus. Gesendet wird nur über „Jetzt übertragen“.',
    label: (pakete: number, dateien: number) =>
      `Noch nicht übertragen: ${pakete} Pakete, ${dateien} Dateien`,
    state: {
      leer: 'Alles übertragen.',
      wartet: 'Wartet auf den nächsten Versuch. Die App wiederholt ihn von selbst.',
      sendet: 'Überträgt gerade …',
      steckt: 'Einiges kam noch nicht durch und wartet hier. Die Bewertungen sind gesichert.',
    },
  },
  header: {
    stationPrefix: 'Station',
    candidate: 'Prüfling',
    /** Steht im Kopf, solange der Ausweis noch nicht gescannt ist. */
    noCandidate: 'kein Prüfling',
    remaining: 'Restzeit',
    total: 'Gesamt',
    settings: 'Einstellungen',
    chooseExam: 'Prüfung und Station wählen',
    startClock: 'Uhr weiterlaufen lassen',
    pauseClock: 'Uhr anhalten',
    resetClock: 'Zeit zurücksetzen',
    setClock: 'Stationszeit einstellen',
    problemExam: 'Prüfung am Server geändert oder entfernt',
  },

  bogen: {
    vignette: 'Fallvignette',
    open: 'Öffnen',
    task: 'Aufgabe',
    comment: 'Kommentar',
    comments: (n: number) => (n === 1 ? '1 Kommentar' : `${n} Kommentare`),
    fullScore: 'volle Punkte',
    noScore: 'keine Punkte',
    reduced: 'reduziert',
    adjusted: 'angepasst',
    pending: 'noch offen',
    possible: (points: string) => `${points} P. möglich`,
    ofPoints: (value: string, max: string) => `${value} / ${max} P.`,
    points: (value: string) => `${value} P.`,
    progress: (done: number, total: number) => `${done} von ${total} Aufgaben bewertet`,
    commentsOn: (tasks: string) => `Kommentare an Aufgabe ${tasks}`,
    submit: 'Bewertung absenden',
    stationComment: 'Stationskommentar',
    stationCommentOn: (n: number) => `Stationskommentar · ${n}`,
    submitArm: 'Nochmals tippen zum Abgeben',
    submitted: 'Abgesendet',
    emptyTitle: 'Kein Bogen geladen',
    emptyNote:
      'Für diese Station liegt kein Bewertungsbogen vor. Wähle eine andere Station oder lade die Prüfung erneut.',
    chooseStation: 'Station wählen',
  },

  score: {
    upTo: (max: string) => `bis ${max} Punkte`,
    undoOverride: 'Eingriff zurücknehmen — wieder rechnen lassen',
    criterionOf: (index: number, total: number) => `Kriterium ${index} von ${total}`,
    clearChoice: 'Wahl aufheben',
  },

  aufsicht: {
    kicker: 'Prüfungsleitung',
    titel: 'Mitteilung',
    gelesen: 'Gelesen',
    zugabe: (min: number, sek: number) =>
      `Prüfungsleitung: +${[min > 0 ? `${min} Min.` : '', sek > 0 ? `${sek} s` : ''].filter(Boolean).join(' ')} Stationszeit`,
  },
  timer: {
    title: 'Stationszeit',
    running: 'läuft',
    paused: 'angehalten',
    expired: 'abgelaufen',
    resume: 'Weiterlaufen',
    pause: 'Anhalten',
    reset: 'Zurücksetzen',
    minutes: (n: number) => `${n} min`,
    /** Die Abschnitte des Zirkeltakts — sie stehen statt „Restzeit" im Kopf. */
    fromStation: 'Station',
    hide: 'Restzeit ausblenden',
  },


  unbekannteEinstellungen: (namen: string) => `Unbekannte Einstellungen übergangen: ${namen}`,
  aufnahmeSymbol: 'Bildschirmfoto für die Prüfungsleitung aufgenommen',
  preview: {
    laedt: 'Vorschau wird geladen …',
    notFound: 'Diese Vorschau gibt es nicht mehr — schon abgeholt oder abgelaufen. In Relay neu anlegen.',
    network: 'Keine Verbindung zum Vorschaudienst. Netz prüfen und erneut scannen.',
    clock: 'Die Uhr dieses Geräts geht falsch. Uhrzeit richten und erneut scannen.',
    signature: 'Der Vorschaudienst hat dieses Gerät abgewiesen.',
    decrypt: 'Die Vorschau liess sich nicht entschlüsseln.',
    version: 'Diese Vorschau stammt aus einer neueren Fassung. Bitte die App aktualisieren.',
    tooLarge: 'Die Vorschau ist zu gross für dieses Gerät.',
    format: 'Die Vorschau ist unvollständig oder beschädigt.',
    realExam: 'Diese Prüfung liegt schon als echte Prüfung auf dem Gerät. Die Vorschau überschreibt sie nicht.',
    unbekannt: 'Die Vorschau liess sich nicht öffnen.',
  },
  qr: {
    kicker: 'QR-Code',
    title: 'Einstellungen übernehmen?',
    scan: 'QR-Code scannen',
    scanLang: 'Einstellungen oder Auswahl scannen',
    scanHinweis: 'Einrichtung oder Auswahl',
    uebernimmt: (n: number) => (n === 1 ? 'Eine Einstellung wird gesetzt:' : `${n} Einstellungen werden gesetzt:`),
    zugang: 'Serverzugang',
    uebrige: 'Weitere Einstellungen',
    warnung: 'Ein gescannter Code gilt vor den Vorgaben der Geräteverwaltung. Er trägt den Serverzugang im Klartext — ein Ausdruck davon gehört nicht an eine Pinnwand.',
    ignoriert: (n: number) => (n === 1 ? 'Ein Eintrag ohne Entsprechung' : `${n} Einträge ohne Entsprechung`),
    nichts: 'Aus diesem Code lässt sich keine Einstellung übernehmen.',
    uebernehmen: 'Übernehmen',
    abbrechen: 'Abbrechen',
    gesetzt: 'Einstellungen übernommen.',
    auswahlGesetzt: 'Auswahl übernommen.',
    examFehlt: 'Die Prüfung aus dem Code liegt nicht auf diesem Gerät. Erst herunterladen, dann erneut scannen.',
    stationFehlt: 'Station, Prüfer oder Schauspieler aus dem Code gibt es in dieser Prüfung nicht.',
    fremd: 'Dieser QR-Code gehört nicht zu fairexa Stage.',
    kaputt: 'Der QR-Code ist beschädigt oder unvollständig.',
    fassung: 'Dieser Code stammt aus einer neueren Fassung der App.',
    geraeteart: 'Dieser Code ist für eine andere Geräteart.',
    leer: '(leer)',
    gruppe: (name: string) => `Gruppe: ${name}`,
    verwerfen: 'Gescannte Einstellungen zurücknehmen',
    namen: {
      server_url: 'Serveradresse',
      classic_login: 'Anmeldename',
      classic_password: 'Passwort',
      server_protocol: 'Protokoll',
      settings_password: 'Passwort vor den Einstellungen',
      language: 'Sprache',
      sheet_half_points: 'Halbe Punkte',
      sheet_show_timer: 'Stationsuhr',
      sheet_show_actors: 'Schauspieler',
      save_photos_in_album: 'Kopie in Fotos',
      min_brightness: 'Helligkeit, dunkelste Stufe',
    } as Partial<Record<string, string>>,
  },
  setup: {
    presenceLoaded: 'geladen',
    presenceServer: 'zum Download',
    presenceLocal: 'nicht mehr auf dem Server',
    warnStale: 'Am Server geändert — zum Übernehmen löschen und neu herunterladen. Erfasste Bewertungen gehen dabei verloren.',
    simulativ: 'Simulationsprüfung',
    simulativNote: 'Nichts wird aufgezeichnet — keine Bewertung, kein Kommentar, keine Aufnahme verlässt dieses Gerät.',
    vorschau: 'Vorschau',
    vorschauNote: 'Aus Relay zum Durchspielen. Nichts wird gesendet. Wenn fertig, von Hand löschen.',
    warnGone: 'Diese Prüfung kennt der Server nicht mehr. Bewertungen lassen sich nicht mehr übertragen.',
    showAll: 'Alle Prüfungen anzeigen',
    download: 'Herunterladen',
    downloading: 'Lädt…',
    deleteExam: 'Von diesem Gerät löschen',
    pruefungsEinstellungen: 'Einstellungen dieser Prüfung',
    pruefungsEinstellungenNote: 'Gelten, solange diese Prüfung ausgewählt ist:',
    einstellungAn: 'an',
    einstellungAus: 'aus',
    deleteArm: 'Nochmals tippen zum Löschen',
    deleteTitle: 'Prüfung löschen?',
    deleteBody: (name: string) => `„${name}" wird vollständig von diesem Gerät entfernt.`,
    deleteRatings: (prüflinge: number, offen: number) =>
      `Dazu stehen Bewertungen von ${prüflinge} ${prüflinge === 1 ? 'Prüfling' : 'Prüflingen'}` +
      (offen > 0
        ? ` — davon sind ${offen} ${offen === 1 ? 'Eintrag' : 'Einträge'} noch nicht übertragen und dann unwiederbringlich verloren.`
        : ' — alle sind bereits übertragen.'),
    deleteConfirm: 'Endgültig löschen',
    deleteArchiveNote: 'Die Aufnahmen bleiben vorerst auf dem Gerät und werden nach der eingestellten Frist endgültig gelöscht.',
    deleteCancel: 'Abbrechen',
    downloadFailed: 'Herunterladen fehlgeschlagen',
    refreshFailed: 'Der Server ist nicht erreichbar',
    chooseTitle: 'Prüfung einrichten',
    kicker: 'Prüfung einrichten',
    exam: 'Prüfung',
    pickTitle: 'Prüfungsauswahl',
    refresh: 'Aktualisieren',
    loaded: 'geladen',
    notLoaded: 'nicht geladen',
    station: 'Station',
    stations: (n: number) => (n === 1 ? '1 Station' : `${n} Stationen`),
    examiner: 'Prüfer',
    examiners: (n: number) => (n === 1 ? '1 Prüfer' : `${n} Prüfer`),
    actor: 'Schauspieler',
    actors: (n: number) => (n === 1 ? '1 Schauspieler' : `${n} Schauspieler`),
    close: 'Schließen',
    apply: 'Übernehmen',
  },

  candidates: {
    kicker: 'Prüfling wechseln',
    summary: (total: number, rated: number) => `${total} Prüflinge · ${rated} bewertet`,
    search: 'Name, Matrikel- oder Prüfungsnr.',
    searchLabel: 'Prüfling suchen',
    sortBy: 'Sortieren',
    sort: {
      nachname: 'Nachname',
      vorname: 'Vorname',
      matrikel: 'Matrikelnr.',
      pruefungsnr: 'Prüfungsnr.',
    },
    filterAll: 'Alle',
    /**
     * ⚠️ Steht **statt** „läuft“ an einem angefangenen Bogen: dieselbe Auskunft, nur
     * genauer (`docs/oberflaeche-luecken.md`, Punkt 2).
     */
    offeneAufgaben: (n: number) => (n === 1 ? '1 offen' : `${n} offen`),
    komplett: 'vollständig',
    filterOpen: 'Offen',
    filterRated: 'Bewertet',
    note: 'Beim Wechsel bleibt die bisherige Eingabe auf dem Gerät erhalten und wird beim Zurückwechseln wieder angezeigt.',
    empty: 'Kein Prüfling passt zu Suche und Filter.',
    scan: 'QR-Code scannen',
    close: 'Schließen',
    state: {
      wartet: 'wartet',
      laeuft: 'läuft',
      bewertet: 'bewertet',
      entschuldigt: 'entschuldigt',
    },
  },

  switchCandidate: {
    kicker: 'Prüfling wechseln',
    withDraft: (name: string) => `Die bisherige Eingabe für ${name} wird wieder angezeigt.`,
    blank: 'Es öffnet sich ein leerer Bogen, die Stationsuhr beginnt von vorn.',
    kept: (name: string) => `Die Eingabe für ${name} bleibt auf dem Gerät erhalten.`,
    cancel: 'Abbrechen',
    confirm: 'Wechseln',
  },

  videoAnnotate: {
    play: 'Abspielen',
    pause: 'Anhalten',
    save: 'Sichern',
    collapse: 'Leisten einklappen',
    expand: 'Leisten ausklappen',
    cancel: 'Abbrechen',
    textPlaceholder: 'Beschriftung',
    band: 'Band',
    addBand: 'Band hinzufügen',
    deleteBand: 'Band löschen',
    open: 'Markieren',
    notPlayable: 'Diese Aufnahme lässt sich hier nicht öffnen.',
    soundOn: 'Ton an',
    soundOff: 'Ton aus',
  },
  annotate: {
    title: 'Markierungen',
    hint: 'Werkzeug wählen und auf dem Bild ziehen. Doppeltippen auf eine Beschriftung ändert ihren Text.',
    toolArrow: 'Pfeil',
    toolEllipse: 'Kreis',
    toolRect: 'Rechteck',
    toolFreehand: 'Freihand',
    toolText: 'Text',
    toolNumber: 'Nummer',
    toolCover: 'Deckfläche',
    color: 'Farbe',
    size: 'Größe',
    sizeSmall: 'klein',
    sizeMedium: 'mittel',
    sizeLarge: 'groß',
    undo: 'Rückgängig',
    redo: 'Wiederherstellen',
    deleteMarker: 'Marke löschen',
    clearAll: 'Alle Marken löschen',
    clearAllAsk: 'Alle Marken auf diesem Bild löschen?',
    textPrompt: 'Beschriftung',
    textPlaceholder: 'Text eingeben …',
    readError: 'Die vorhandene Markierungsebene ist unlesbar. Bearbeiten ist gesperrt, damit nichts verloren geht.',
    markerCount: (n: number) => (n === 1 ? '1 Marke' : `${n} Marken`),
    close: 'Fertig',
    cancel: 'Abbrechen',
    ok: 'Übernehmen',
  },
  comment: {
    kicker: (task: string) => `Kommentar · Aufgabe ${task}`,
    none: 'Noch kein Kommentar',
    entries: (n: number) => (n === 1 ? '1 Eintrag' : `${n} Einträge`),
    done: 'Fertig',
    tabText: 'Text & Bausteine',
    tabHandwriting: 'Handschrift',
    tabAudio: 'Audio',
    tabPhoto: 'Foto',
    tabVideo: 'Video',
    snippets: 'Bausteine',
    snippetCount: (n: number) => `${n} Textbausteine`,
    freeText: 'Freitext',
    freeTextHint: 'Freitext eingeben oder Bausteine links antippen …',
    /** ⚠️ Ohne Bausteine darf der Platzhalter keine versprechen — die Prüfung führt dann keine. */
    freeTextHintOhne: 'Freitext eingeben …',
    clear: 'Leeren',
    handwritingTitle: 'Handschriftlicher Kommentar',
    handwritingHint: 'Direkt hier schreiben.',
    recordReady: 'bereit',
    recordRunning: 'nimmt auf',
    recordStart: 'Aufnahme starten',
    recordStop: 'Aufnahme beenden',
    noRecording: 'Noch keine Aufnahme.',
    recordingAt: (time: string) => `Aufnahme ${time}`,
    saved: 'gesichert',
    noFile: 'noch keine Datei',
    play: 'Abspielen',
    pausePlayback: 'Wiedergabe anhalten',
    deleteRecording: 'Aufnahme löschen',
    takePhoto: 'Foto aufnehmen',
    cameraOpening: 'Kamera öffnet …',
    photoCount: (n: number) => (n === 1 ? '1 Foto' : `${n} Fotos`),
    noPhoto: 'Noch kein Foto.',
    photoAt: (time: string) => `Foto ${time}`,
    viewPhoto: 'Foto ansehen',
    deletePhoto: 'Foto löschen',
    cameraUnavailable: (message: string) => `Kamera nicht verfügbar: ${message}`,
    recordVideo: 'Video aufnehmen',
    videoCount: (n: number) => (n === 1 ? '1 Video' : `${n} Videos`),
    mergingVideo: 'Teilaufnahmen werden zusammengefügt …',
    noVideo: 'Noch kein Video.',
    videoAt: (time: string) => `Video ${time}`,
    playVideo: 'Video abspielen',
    deleteVideo: 'Video löschen',
    deleteKicker: 'Wegräumen',
    deleteTitle: {
      video: 'Videoaufnahme wegräumen?',
      photo: 'Foto wegräumen?',
      audio: 'Tonaufnahme wegräumen?',
      handwriting: 'Handschrift verwerfen?',
    },
    deleteArchived: 'Die Aufnahme wandert ins Archiv und wird nach der eingestellten Frist endgültig gelöscht.',
    deleteGone: 'Die Striche sind danach weg — sie liegen im Bogen, nicht als Datei.',
    deleteConfirm: 'Wegräumen',
    deleteCancel: 'Abbrechen',
    handwritingStrokes: (n: number) => (n === 1 ? '1 Strich' : `${n} Striche`),
    micDenied: 'Ohne Mikrofonzugriff ist keine Aufnahme möglich.',
    close: 'Schließen',
  },

  lock: {
    kicker: 'Geschützt',
    title: 'Einstellungen',
    hint: 'Die Einstellungen dieser App sind mit einem Passwort geschützt. Es steht in den iPad-Einstellungen unter „fairexa Stage“.',
    password: 'Passwort',
    open: 'Öffnen',
    wrong: 'Das Passwort stimmt nicht.',
  },

  pruefungsPasswort: {
    kicker: 'Prüfungspasswort',
    laden: 'Herunterladen',
    loeschen: 'Löschen',
    hint: 'Diese Prüfung ist mit einem Passwort geschützt. Die Prüfungsleitung kennt es.',
    password: 'Passwort',
    weiter: 'Weiter',
    wrong: 'Das Passwort stimmt nicht.',
  },

  /**
   * Die Abfrage vor dem Beenden — **nur am Rechner** (`features/beenden/`).
   *
   * Die vier `menu…`-Zeilen beschriften das Menü der Hülle. Sie stehen hier und nicht im
   * Hauptprozess, damit die Sprache an einem Ort entschieden wird.
   */
  exit: {
    kicker: 'Prüfungsclient',
    title: 'Beenden',
    hint: 'Zum Beenden die PIN eingeben. Sie steht in den Geräteeinstellungen unter „fairexa Stage“.',
    pin: 'PIN',
    quit: 'Beenden',
    cancel: 'Weiterprüfen',
    wrong: 'Die PIN stimmt nicht.',
    button: 'Beenden',
    menu: 'Prüfung beenden …',
    menuFile: 'Datei',
    menuEdit: 'Bearbeiten',
    menuView: 'Ansicht',
  },

  vignette: {
    situation: 'Situation',
    task: 'Auftrag',
    instructions: 'Instruktionen für Prüfer:innen',
    material: 'Material',
    materialMissing: 'Nicht auf dem Gerät',
  },

  clock: {
    ahead: 'vor',
    behind: 'nach',
    hour: 'Stunde',
    hours: 'Stunden',
    minute: 'Minute',
    minutes: 'Minuten',
    seconds: 'Sekunden',
  },
  settings: {
    groupDevice: 'Dieses Gerät',
    deviceName: 'Name',
    deviceNamePlaceholder: 'z. B. Prüfungsgerät 3',
    deviceNameNote: 'Der Name geht in jede Beglaubigung ein und steht später neben jeder Aufnahme dieses Geräts.',
    deviceId: 'Kennung',
    clockOffTitle: 'Die Uhr dieses Geräts geht falsch',
    clockOffBody: (abweichung: string) =>
      `Sie weicht um ${abweichung} von der Netzzeit ab. Zeitstempel dieses Geräts sind dadurch falsch.`,
    clockOffFix: 'In den Einstellungen des Geräts unter „Datum & Uhrzeit" korrigieren.',
    clockOffClose: 'Verstanden',
    title: 'Darstellung und Bogen',
    kicker: 'Einstellungen',
    accent: 'Akzentfarbe',
    language: 'Sprache',
    managedByMdm: 'Von der Geräteverwaltung vorgegeben — hier nicht änderbar.',
    passwortStand: (gesetzt: boolean): string =>
      gesetzt ? 'Ein Passwort ist gesetzt.' : 'Kein Passwort gesetzt – die Einstellungen stehen jedem offen.',
    passwortNeu: 'Neues Passwort',
    passwortWiederholen: 'Wiederholen',
    passwortSetzen: 'Passwort setzen',
    passwortEntfernen: 'Passwort entfernen',
    passwortUngleich: 'Die beiden Eingaben stimmen nicht überein.',
    passwortGesetzt: 'Gesetzt. Es gilt, sobald die App in den Hintergrund geht oder das Gerät einschläft.',
    passwortEntfernt: 'Entfernt.',
    passwortAusQr: 'Von einem QR-Code vorgegeben — hier nicht änderbar.',
    brightness: 'Helligkeit',
    brightnessFixed: 'Von der Aufsicht festgelegt',
    languageNote: 'Gilt nur für die Oberfläche. Prüfungsinhalte bleiben, wie sie eingegeben wurden.',
    erklaerung: 'Erklärung',
    ausPruefung: 'Von der Prüfung vorgegeben',
    inPruefungGeaendert: 'Für diese Prüfung geändert — gilt bis zum Prüfungswechsel',
    groupSheet: 'Bogen',
    halfPoints: 'Halbe Punkte zulassen',
    halfPointsNote: 'Das Punkte-Blatt zeigt dann 0,5er-Schritte.',
    showTimer: 'Restzeit im Kopf anzeigen',
    showTimerNote: 'Antippen öffnet Start, Anhalten und Zurücksetzen.',
    groupRecording: 'Aufnahme',
    videoMaxNote: 'Obergrenze für ein Video, über alle Teilaufnahmen zusammen.',
    minutes: (n: number) => (n === 1 ? '1 Minute' : `${n} Minuten`),
    groupArchive: 'Archiv gelöschter Prüfungen',
    archiveNote: 'So lange bleiben Aufnahmen einer gelöschten Prüfung noch auf dem Gerät.',
    months: (n: number) => (n === 1 ? '1 Monat' : `${n} Monate`),
    groupAfterSubmit: 'Nach dem Absenden',
    scanner: 'Danach QR-Scanner öffnen',
    scannerNote: 'Sonst wird der nächste Prüfling aus der Liste gewählt.',
    haptics: 'Haptisches Feedback',
    hapticsNote: 'Kurze Rückmeldung bei jedem Tipp auf eine Checklistenzeile.',
    groupTrial: 'Probelauf',
  },

  server: {
    group: 'Server',
    url: 'Adresse',
    urlPlaceholder: 'https://relay.example:8111',
    login: 'Anmeldename',
    password: 'Passwort',
    save: 'Sichern und testen',
    testing: 'Teste …',
    connected: (zeit: string) => `Zugang gilt. Serverzeit ${zeit}.`,
    connectedOhneZeit: 'Zugang gilt. Dieser Server nennt keine Zeit — der Versatz bleibt ungemessen.',
    drift: (sekunden: number) => `Achtung: Die Uhr dieses Geräts weicht um ${sekunden} s vom Server ab.`,
    failed: (grund: string) => `Keine Verbindung: ${grund}`,
    none: 'Kein Server eingerichtet — die App arbeitet allein auf diesem Gerät.',
    sendNow: 'Jetzt übertragen',
    sent: (n: number) => `${n} übertragen`,
    /**
     * ⚠️ Kein stiller Fehler: ohne Stufen und Punkte je Antwortzeile sähe der Bogen normal
     * aus und die Summe wäre falsch (`docs/auftrag-relay-antwortfelder.md`).
     */
    downloadOhnePunkte:
      'Die Prüfung kam ohne Punktzahlen an — der Server liefert zu den Antwortzeilen weder Stufen noch Punkte. Der Bogen zeigt sie als Checkliste mit je einem Punkt. Bitte den Prüfungsleiter verständigen, bevor damit bewertet wird.',
    mode: 'Protokoll',
    messageFromServer: (text: string) => `Nachricht vom Server: ${text}`,
    sendStuck: (n: number) =>
      n === 1
        ? '1 Eintrag lässt sich nicht übertragen — Näheres im Protokoll.'
        : `${n} Einträge lassen sich nicht übertragen — Näheres im Protokoll.`,
    passwordVolatile: 'Das Passwort liess sich nicht dauerhaft sichern (Schlüsselbund). Es gilt nur für diese Sitzung.',
    connect: 'Zugang testen',
  },

  submit: {
    kicker: 'Bewertung absenden',
    noComment: '—',
    totalLabel: 'Gesamt',
    openTasks: (names: string, count: number) =>
      count === 1
        ? `Eine Aufgabe ist noch nicht bewertet: ${names}.`
        : `${count} Aufgaben sind noch nicht bewertet: ${names}.`,
    screenshotNote: 'Ein Bildschirmfoto des Bogens wird gesichert und dem Prüfling zugeordnet. ',
    cancel: 'Abbrechen',
    confirm: 'Absenden',
  },

  locked: {
    kicker: 'Abgesendet',
    title: 'Bewertung abgesendet',
    meta: (score: string, max: string, at: string) => `${score} / ${max} Punkte · ${at} Uhr`,
    /** Ohne Gesamtpunkte (`show_sum_points` aus). */
    metaOhnePunkte: (at: string) => `${at} Uhr`,
    note: 'Der Bogen ist gesperrt und bleibt zur Kontrolle sichtbar.',
    next: (name: string) => `Weiter zu ${name}`,
    scanNext: 'Nächster Prüfling scannen',
    fromList: 'Nächster aus Liste',
  },

  /**
   * Kurze Einblendungen (`Toast`) zur Sicherung: `show()` bekommt einen Schlüssel aus diesem
   * Zweig, nie fertigen Text — sonst würde ein Hinweis am Katalog vorbeilaufen und stünde auf
   * Deutsch da, selbst wenn der Prüfer Englisch oder Französisch eingestellt hat.
   */
  /**
   * Die Belegübergabe im Einstellungsblatt (`features/sheets/BelegeGruppe.tsx`) — **nur im
   * Browser**. Am iPad gehen dieselben Dateien über Finder oder iTunes hinaus.
   */
  belege: {
    group: 'Belege',
    holen: 'Belege als ZIP sichern',
    laeuft: 'Wird gepackt …',
    hinweis:
      'Aufnahmen, Bogenabzüge und Beglaubigungen dieses Browsers — einschliesslich des Archivs gelöschter Prüfungen. Auf dem iPad stehen dieselben Dateien über den Finder bereit.',
    leer: 'Es liegen keine Belege vor.',
    fertig: (dateien: number, mb: number) =>
      dateien === 1 ? `1 Datei gesichert (${mb} MB).` : `${dateien} Dateien gesichert (${mb} MB).`,
    fehler: 'Das Sichern ist gescheitert:',
  },

  persistence: {
    resumed: 'Stand wiederhergestellt',
    writeFailed: 'Konnte nicht gesichert werden — bitte den Prüfungsleiter verständigen',
    deleteFailed: 'Datei konnte nicht gelöscht werden — Eintrag bleibt entfernt',
    /** Einzige Meldung, die ein Prüfer bei gescheitertem Start überhaupt zu sehen bekommt
     *  (`main.tsx`) — kein Toast, sondern der ganze Bildschirminhalt. */
    startupFailed:
      'Die Prüfung konnte nicht geladen werden. Bitte die App neu starten oder den Prüfungsleiter verständigen.',
    /** Nach dem Schalter in den iPad-Einstellungen (`server/demoSchalter.ts`). */
    demoInstalled: 'Demo-Prüfung installiert',
    /** Beim Start am Desktop: der Belegordner liegt zu tief (`platform/pfadlaenge.ts`). */
    pfadZuLang:
      'Der Belegordner liegt zu tief — Aufnahmen könnten nicht gesichert werden. Bitte den Prüfungsleiter verständigen.',
  },

  /**
   * Die Schlafscheibe (`features/schlaf/`). Der Knopf trägt im Wachzustand nur sein Symbol —
   * `sleep` ist dann seine Vorlesebeschriftung.
   */
  sleep: {
    sleep: 'Bildschirm abdunkeln',
    wake: 'Aufwachen',
    wakeHint: 'Zum Weiterarbeiten hier antippen',
  },

  scanner: {
    title: 'Nächsten Prüfling scannen',
    window: 'Zeitfenster',
    hint: 'QR-Code des Prüflings in den Rahmen halten',
    unknown: (code: string) => `Kein Prüfling zu diesem QR-Code: ${code}`,
    einrichtungscode: 'Das ist ein Einrichtungscode — bitte im Einrichtungsblatt scannen, nicht hier.',
    cancel: 'Abbrechen',
    scan: 'QR-Code scannen',
    noCamera: 'Ohne Kamera: nächster Prüfling',
    ariaLabel: 'QR-Code scannen',
    timeoutTitle: 'Kein QR-Code erkannt',
    timeoutText: (seconds: number) =>
      `Nach ${seconds} Sekunden ohne Scan: Prüfling abmelden und Station freigeben, oder zurück zum letzten Bogen.`,
    back: 'Zurück zum Bogen',
    logout: 'Prüfling abmelden',
  },
};
// Bewusst **ohne** `as const`: sonst wären die Typen die wörtlichen deutschen Zeichenketten,
// und keine Übersetzung ließe sich zuweisen.

/** Die Form, an die sich alle Sprachen halten müssen. */
export type Messages = typeof de;
